/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Spin, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import parse from 'html-react-parser';
import IconNoData from '../../../assets/Images/icons/icon-no-data';
import { Pie } from '@ant-design/charts';

export default function Acceptance({ acceptanceDetail, isLoading }) {
    const { t, i18n } = useTranslation();
    let lang = i18n.language;
    const [cookieInfo, setCookieInfo] = useState([]);

    useEffect(() => {
        if (acceptanceDetail) {
            setCookieInfo([
                {
                    type: t('COOKIE.AcceptedAll'),
                    value: acceptanceDetail?.find(
                        (e) => e.type?.toLowerCase() === 'accepted all'
                    )?.value,
                    total: acceptanceDetail?.find(
                        (e) => e.type?.toLowerCase() === 'accepted all'
                    )?.total,
                },
                {
                    type: t('COOKIE.PartiallyAccepted'),
                    value: acceptanceDetail?.find(
                        (e) => e.type?.toLowerCase() === 'partially accepted'
                    )?.value,
                    total: acceptanceDetail?.find(
                        (e) => e.type?.toLowerCase() === 'partially accepted'
                    )?.total,
                },
                {
                    type: t('COOKIE.RejectedAll'),
                    value: acceptanceDetail?.find(
                        (e) => e.type?.toLowerCase() === 'rejected all'
                    )?.value,
                    total: acceptanceDetail?.find(
                        (e) => e.type?.toLowerCase() === 'rejected all'
                    )?.total,
                },
                {
                    type: t('COOKIE.Informed'),
                    value: acceptanceDetail?.find(
                        (e) => e.type?.toLowerCase() === 'informed'
                    )?.value,
                    total: acceptanceDetail?.find(
                        (e) => e.type?.toLowerCase() === 'informed'
                    )?.total,
                },
                {
                    type: t('COOKIE.Dismiss'),
                    value: acceptanceDetail?.find(
                        (e) => e.type?.toLowerCase() === 'dismiss'
                    )?.value,
                    total: acceptanceDetail?.find(
                        (e) => e.type?.toLowerCase() === 'dismiss'
                    )?.total,
                },
            ]);
        }
    }, [acceptanceDetail, lang]);

    const config = useMemo(() => {
        return {
            data: cookieInfo?.reverse(),
            angleField: 'value',
            colorField: 'type',
            color: ({ type }) => {
                switch (type) {
                    case t('COOKIE.AcceptedAll'):
                        return '#826AF9';
                    case t('COOKIE.PartiallyAccepted'):
                        return '#9E86FF';
                    case t('COOKIE.RejectedAll'):
                        return '#D0AEFF';
                    case t('COOKIE.Informed'):
                        return '#D652F2';
                    case t('COOKIE.Dismiss'):
                        return '#EA89FF';
                    default:
                        return '#EA89FF';
                }
            },
            radius: 1,
            width: 200,
            height: 200,
            innerRadius: 0.6,
            label: false,
            legend: false,
            interactions: [
                {
                    type: 'element-active',
                },
            ],
            statistic: false,
        };
    }, [cookieInfo]);

    return (
        <Spin spinning={isLoading}>
            <div className="flex space-x-2 items-center pb-4 justify-between">
                <div className="font-semibold text-xl">
                    {t('COOKIE.Acceptance')}
                </div>
                <div>
                    <Tooltip
                        placement="right"
                        overlayClassName="min-w-[300px]"
                        title={parse(t('COOKIE.ShowsConsentForEach'))}
                    >
                        <a className="bg-purple-light p-2 flex items-center justify-center rounded-lg text-purple">
                            <ExclamationCircleOutlined />
                        </a>
                    </Tooltip>
                </div>
            </div>

            {acceptanceDetail?.length <= 0 ? (
                <div className="text-center text-sm space-y-2 my-32">
                    <IconNoData />
                    <p className="text-[#65707A] text-[14px]">
                        {t('common.noData')}
                    </p>
                </div>
            ) : (
                <>
                    <Pie {...config} />
                    <div className="flex justify-between pt-4 pb-2 ">
                        <div className="flex items-center">
                            <svg
                                width="12"
                                height="13"
                                viewBox="0 0 12 13"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <circle cx="6" cy="6.5" r="6" fill="#826AF9" />
                            </svg>
                            <div className="pl-2">
                                {t('COOKIE.AcceptedAll')}
                            </div>
                        </div>
                        <div className="text-gray-500">
                            {acceptanceDetail?.find(
                                (e) => e.type?.toLowerCase() === 'accepted all'
                            )?.value || 0}
                        </div>
                    </div>

                    <div className="flex justify-between pb-2">
                        <div className="flex items-center">
                            <svg
                                width="12"
                                height="13"
                                viewBox="0 0 12 13"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <circle cx="6" cy="6.5" r="6" fill="#9E86FF" />
                            </svg>
                            <div className="pl-2">
                                {t('COOKIE.PartiallyAccepted')}
                            </div>
                        </div>
                        <div className="text-gray-500">
                            {acceptanceDetail?.find(
                                (e) =>
                                    e.type?.toLowerCase() ===
                                    'partially accepted'
                            )?.value || 0}
                        </div>
                    </div>

                    <div className="flex justify-between pb-2">
                        <div className="flex items-center">
                            <svg
                                width="12"
                                height="13"
                                viewBox="0 0 12 13"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <circle cx="6" cy="6.5" r="6" fill="#D652F2" />
                            </svg>
                            <div className="pl-2">
                                {t('COOKIE.RejectedAll')}
                            </div>
                        </div>
                        <div className="text-gray-500">
                            {acceptanceDetail?.find(
                                (e) => e.type?.toLowerCase() === 'rejected all'
                            )?.value || 0}
                        </div>
                    </div>

                    <div className="flex justify-between pb-2">
                        <div className="flex items-center">
                            <svg
                                width="12"
                                height="13"
                                viewBox="0 0 12 13"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <circle cx="6" cy="6.5" r="6" fill="#D652F2" />
                            </svg>
                            <div className="pl-2">{t('COOKIE.Informed')}</div>
                        </div>
                        <div className="text-gray-500">
                            {acceptanceDetail?.find(
                                (e) => e.type?.toLowerCase() === 'informed'
                            )?.value || 0}
                        </div>
                    </div>

                    <div className="flex justify-between pb-2">
                        <div className="flex items-center">
                            <svg
                                width="12"
                                height="13"
                                viewBox="0 0 12 13"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <circle cx="6" cy="6.5" r="6" fill="#EA89FF" />
                            </svg>
                            <div className="pl-2">{t('COOKIE.Dismiss')}</div>
                        </div>
                        <div className="text-gray-500">
                            {acceptanceDetail?.find(
                                (e) => e.type?.toLowerCase() === 'dismiss'
                            )?.value || 0}
                        </div>
                    </div>

                    <div
                        className="pt-2"
                        style={{ borderTop: '0.5px solid #E9EBF1' }}
                    >
                        <div className="font-semibold">
                            {parse(
                                t('Cookie Consent Times', {
                                    length: acceptanceDetail[0]?.total || 0,
                                })
                            )}
                        </div>
                    </div>
                </>
            )}
        </Spin>
    );
}
